import { useEffect, useMemo, useRef, useState } from "react";
import { Download, Info, Loader2, PackageOpen, Search, TrendingDown, TrendingUp } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { KeywordCombobox } from "@/components/shared/KeywordCombobox";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { TruncatedText } from "@/components/ui/truncated-text";
import { PageTitle } from "@/components/shared/PageTitle";
import { useAppData } from "@/contexts/AppDataContext";
import { CopyVisualButton } from "@/components/shared/CopyVisualButton";
import { SearchFilterBar } from "@/components/shared/SearchFilterBar";
import { CombinedFilterDropdown, type TextFilterColumn } from "@/components/shared/CombinedFilterDropdown";
import type { RangeFilterColumn, RangeFilterState } from "@/components/keyword-bank/constants";
import { cn } from "@/lib/utils";
import { SUGGESTION_MIN_CHARS, useKeywordSuggestions, useKeywordTrends } from "@/resources/keyword-trends/keyword-trends.api";
import { exportKeywordTrends } from "@/utils/keywordTrendsExport";
import { MONTHS, filterKeywordRows, formatVolume as fmt, formatWeekTick as weekTick, growthWindows, isIsolatedPoint, keywordRows, seasonalOverlay, weeklyTotals } from "@/utils/keywordTrends";
import type { KeywordTrendMatchMode, KeywordTrendSeries } from "@/types/keyword-trends";

/** FR-9: generic examples, so a first-time user can see what the screen does (Kate, AUT-429). */
const SUGGESTED_TERMS = ["sunscreen", "running shoes", "vitamin c supplement", "vacuum machine"];

/**
 * Series colors, `--chart-1` … `--chart-5` in order. This is what the design reference's canonical
 * `TrendLineChart` does (`signal-v3/src/components/shared/TrendLineChart.tsx:56`), and it is why
 * this file no longer carries a bespoke five-colour array: a second palette on one screen is how a
 * design system stops being one.
 */
const seriesColor = (i: number) => `hsl(var(--chart-${(i % 5) + 1}))`;

/**
 * AUT-445. Draws a dot **only** where a reading has no neighbour to connect to.
 *
 * `connectNulls` is off on purpose (see the chart below), which means a reading whose neighbours
 * are both missing draws nothing at all -- a segment needs two points, and dots are off. The value
 * was in the data and in the tooltip, but nothing on the chart. Every other point stays dotless, so
 * a continuous line looks exactly as it did.
 *
 * Recharts calls this for each point and wants an element back either way, so the uninteresting
 * ones return an empty `<g>` rather than `null`.
 */
const isolatedDot = (rows: Array<Record<string, string | number | null>>, key: string) =>
  ({ cx, cy, index, stroke }: { cx?: number; cy?: number; index?: number; stroke?: string }) => {
    const missing = cx == null || cy == null || index == null;

    return missing || !isIsolatedPoint(rows, key, index)
      ? <g key={`${key}-${index}`} />
      : <circle key={`${key}-${index}`} cx={cx} cy={cy} r={3} fill={stroke} stroke={stroke} />;
  };

const CONTINUOUS_CONFIG: ChartConfig = {
  volume: { label: "Weekly volume", color: seriesColor(0) },
};

const TEXT_FILTERS: TextFilterColumn[] = [
  { id: "phrase", label: "Phrases containing", placeholder: "e.g. mineral" },
  { id: "exclude", label: "Exclude keywords", placeholder: "e.g. spray" },
];

const RANGE_FILTERS: RangeFilterColumn[] = [
  { id: "totalVolume", label: "Total volume", field: "total" },
  { id: "weeks", label: "Weeks observed", field: "weeks" },
];

export default function KeywordTrends() {
  const [term, setTerm] = useState("");
  /** What the type-ahead asks about. Trails `term` so a fast typist makes one request, not one
   *  per character -- this reads layer B rather than the warehouse, but it is still a request. */
  const [suggestTerm, setSuggestTerm] = useState("");
  const [mode, setMode] = useState<KeywordTrendMatchMode>("loose");
  const [submitted, setSubmitted] = useState<{ term: string; mode: KeywordTrendMatchMode } | null>(null);
  const [view, setView] = useState<"overlay" | "continuous">("overlay");
  const [tableSearch, setTableSearch] = useState("");
  const [textFilters, setTextFilters] = useState<Record<string, string>>({});
  const [rangeFilters, setRangeFilters] = useState<RangeFilterState>({});
  const chartRef = useRef<HTMLDivElement>(null);

  /**
   * AUT-446, second cause. `flex-1` made the white ground fill the layout column, and that was
   * real -- but the column is not always as tall as the document. The Filters panel is
   * `absolute top-[calc(100%+10px)]` (`CombinedFilterDropdown:295`), and an absolutely positioned
   * element does not grow its parent while it *does* grow the document's scroll height. Measured
   * with one row and the panel open: document 1602, app shell 1418. Those 184px show `body`, whose
   * base rule paints `bg-background` -- `rgb(238, 238, 246)`, the reported grey.
   *
   * Nothing inside the shell can cover that, so the page declares its own ground on the element
   * that really spans the scroll height, and puts it back on the way out. A utility class beats the
   * `@layer base` rule in `index.css`.
   */
  useEffect(() => {
    document.body.classList.add("bg-card");

    return () => document.body.classList.remove("bg-card");
  }, []);

  const { selectedBrandId } = useAppData();

  useEffect(() => {
    const id = window.setTimeout(() => setSuggestTerm(term), 180);

    return () => window.clearTimeout(id);
  }, [term]);

  const { data: suggestionData } = useKeywordSuggestions(selectedBrandId, suggestTerm, true);
  /**
   * Gated on the CURRENT term, not the debounced one the query used. `keepPreviousData` is set
   * globally, so clearing the box leaves the last response in `data` -- without this gate the list
   * from the previous word reappears under an empty field.
   */
  const suggestions = useMemo(
    () => (term.trim().length >= SUGGESTION_MIN_CHARS ? suggestionData?.suggestions ?? [] : []),
    [suggestionData, term],
  );
  const { data, error, isFetching, isError, refetch } = useKeywordTrends(
    selectedBrandId, submitted?.term ?? "", submitted?.mode ?? "loose", submitted !== null,
  );

  const keywords = useMemo(() => data?.keywords ?? [], [data]);
  const totals = useMemo(() => weeklyTotals(keywords), [keywords]);

  const run = (nextTerm: string, nextMode: KeywordTrendMatchMode) => {
    const q = nextTerm.trim();
    if (!q) {
      toast.error("Enter a keyword to search");
      return;
    }
    setTerm(q);
    setMode(nextMode);
    setSubmitted({ term: q, mode: nextMode });
  };

  /** Switching match type re-runs the search that is already on screen, rather than leaving the
   *  control and the results describing two different things. */
  const changeMode = (next: KeywordTrendMatchMode) => {
    setMode(next);
    if (submitted) run(submitted.term, next);
  };

  const reset = () => {
    setTerm(""); setSubmitted(null); setTableSearch(""); setTextFilters({}); setRangeFilters({}); setView("overlay");
  };

  /** FR-4 year-over-year: month on the x-axis, one line per calendar year. */
  const { overlay, years } = useMemo(() => seasonalOverlay(totals), [totals]);

  /** One `--color-<year>` per line, so the legend swatches and strokes come from one source. */
  const overlayConfig = useMemo<ChartConfig>(
    () => Object.fromEntries(years.map((y, i) => [String(y), { label: String(y), color: seriesColor(i) }])),
    [years],
  );

  const totalVolume = useMemo(() => totals.reduce((s, t) => s + t.volume, 0), [totals]);

  /** FR-6: each window against the one before it, over the data actually returned. */
  const growth = useMemo(() => growthWindows(totals), [totals]);

  const allRows = useMemo(() => keywordRows(keywords), [keywords]);

  /** FR-7: filtering is client-side over the returned set -- it must never re-query the warehouse. */
  const rows = useMemo(
    () => filterKeywordRows(allRows, {
      search: tableSearch,
      contains: textFilters.phrase,
      exclude: textFilters.exclude,
      totalVolume: rangeFilters.totalVolume,
      weeks: rangeFilters.weeks,
    }),
    [allRows, tableSearch, textFilters, rangeFilters],
  );

  const capped = data?.capped === true;
  /** `ApiError` carries the status; `error` is typed `Error` here, so this reads it defensively. */
  const throttled = isError && (error as { status?: number } | null)?.status === 429;
  // Both gate on `submitted`, and that is load-bearing rather than belt-and-braces. The query
  // client sets `placeholderData: keepPreviousData` globally (`query-client.ts`), so after Clear --
  // which sets `submitted` to null and moves the key to the empty term -- React Query keeps serving
  // the *previous* search's data. Without this gate the screen showed the pre-search ghost and the
  // old results at the same time, with an empty search box above them.
  const showEmpty = submitted !== null && !isFetching && !isError && keywords.length === 0;
  const showResults = submitted !== null && !isFetching && !isError && keywords.length > 0 && data != null;

  return (
    // `-m-9 p-9 bg-card` is the full-bleed page ground the other top-level screens use
    // (`RankTracker.tsx`, `BrandIntelligence.tsx`, `Products.tsx`) and the design reference sets
    // here too. Without it this screen alone sat on the app background.
    //
    // AUT-446: `flex-1`, not `min-h-[calc(100vh-0px)]`. `SidebarInset` is already
    // `flex min-h-svh flex-col bg-background`, so this is a flex item in a column that is at least
    // the viewport tall -- and a flex item does not grow by default. Whenever the content was
    // shorter than that column (filter the table down to no rows and it is), the white ground
    // stopped at the content and the inset's own grey showed beneath it. `100vh` could not fix
    // that: this element starts 20px above the viewport because of `-m-9`, so a `100vh` floor
    // always ends 20px short, and it ignores the inset's padding besides. Growing to fill the
    // parent is both simpler and exact.
    <div className="-m-9 flex-1 space-y-6 bg-card p-9">
      <div className="space-y-1">
        {/* `brandScoped`, and the subtitle says so: the searchable set is this brand's own keyword
            bank. Both were wrong until 2026-09-24 -- the screen searched the whole US share and
            said so, which was neither what the design reference specifies nor what it now does. */}
        <PageTitle title="Keyword Trends" brandScoped />
        <p className="text-sm text-muted-foreground">
          Search Amazon keyword volume across the brand keyword universe and compare
          year-over-year seasonality.
        </p>
      </div>

      <Card>
        <CardContent className="p-6 space-y-4">
          <form
            className="grid gap-6 md:grid-cols-[minmax(0,1fr)_auto_auto] md:items-end"
            onSubmit={(e) => { e.preventDefault(); run(term, mode); }}
          >
            <div className="space-y-2">
              <Label htmlFor="kw-trend-search">Keyword</Label>
              <KeywordCombobox
                id="kw-trend-search"
                value={term}
                onValueChange={setTerm}
                suggestions={suggestions}
                onSubmit={(next) => run(next, mode)}
                onClear={reset}
                placeholder={'e.g. "sunscreen" or "running shoes"'}
              />
            </div>

            <div className="space-y-2">
              <Label>Match type</Label>
              <RadioGroup
                value={mode}
                onValueChange={(v) => changeMode(v as KeywordTrendMatchMode)}
                className="flex h-9 items-center gap-3"
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem id="kw-match-exact" value="exact" />
                  <Label htmlFor="kw-match-exact" className="font-normal">Exact</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem id="kw-match-loose" value="loose" />
                  <Label htmlFor="kw-match-loose" className="font-normal">Loose (contains)</Label>
                </div>
              </RadioGroup>
            </div>

            {/* Deliberately NOT disabled on an empty term: a greyed-out button leaves the user to
                work out why nothing happens. The design reference keeps it live and says so, which
                is also what makes the empty-term toast in `run` reachable at all. */}
            <Button type="submit" disabled={isFetching} className="h-9 gap-1.5">
              {isFetching
                ? <><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Searching…</>
                : <><Search className="h-4 w-4" aria-hidden="true" /> Search</>}
            </Button>
          </form>

          <Separator />

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground">Try:</span>
            {SUGGESTED_TERMS.map((t) => {
              const active = submitted?.term === t;
              return (
                <Button
                  key={t} type="button" variant="secondary" size="sm" aria-pressed={active}
                  className={cn(
                    "h-8 rounded-full px-3 font-medium text-foreground",
                    active && "border-primary bg-primary-tint-medium hover:bg-primary-tint-strong",
                  )}
                  onClick={() => run(t, mode)}
                >
                  {t}
                </Button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Before the first search the design reference shows a dimmed ghost of the whole screen, so
          an arriving user can see what they are about to get rather than a lone search box on an
          empty page. `aria-hidden` because it is scenery -- there is nothing here to read. */}
      {submitted === null && (
        <div className="space-y-6 opacity-60" aria-hidden="true">
          <div className="grid gap-4 md:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <Card key={i}><CardContent className="p-4 space-y-2"><Skeleton className="h-3 w-28" /><Skeleton className="h-7 w-20" /></CardContent></Card>
            ))}
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <Card key={i}><CardContent className="p-4 space-y-2"><Skeleton className="h-3 w-24" /><Skeleton className="h-7 w-16" /><Skeleton className="h-3 w-32" /></CardContent></Card>
            ))}
          </div>
          <Card><CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <Skeleton className="h-5 w-56" /><Skeleton className="h-8 w-40" />
            </div>
            <Skeleton className="h-8 w-72 rounded-lg" />
            <Skeleton className="h-[280px] w-full rounded-md" />
          </CardContent></Card>
          <Card><CardContent className="p-6 space-y-3">
            <Skeleton className="h-5 w-44" />
            <Skeleton className="h-9 w-64 rounded-lg" />
            <div className="space-y-2 pt-2">
              {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
            </div>
          </CardContent></Card>
        </div>
      )}

      {/* A cold search really does take ~10-15 s (NFR-3), and the skeletons below are almost
          identical to the pre-search ghost -- so without something that *moves*, the screen reads
          as frozen rather than working. Reported from dev on 2026-09-24. `aria-live` so it is
          announced rather than only seen; the skeletons stay `aria-hidden`. */}
      {isFetching && (
        <div
          className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3"
          role="status"
          aria-live="polite"
        >
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-primary" aria-hidden="true" />
          <p className="text-sm text-foreground">
            Searching Amazon keyword volume for <span className="font-medium">“{submitted?.term}”</span>…
            <span className="text-muted-foreground"> This reads three years of weekly data and can take up to 15 seconds.</span>
          </p>
        </div>
      )}

      {isFetching && (
        <div className="space-y-6" aria-hidden="true">
          <div className="grid gap-4 md:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <Card key={i}><CardContent className="p-4 space-y-2"><Skeleton className="h-3 w-28" /><Skeleton className="h-7 w-20" /></CardContent></Card>
            ))}
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <Card key={i}><CardContent className="p-4 space-y-2"><Skeleton className="h-3 w-24" /><Skeleton className="h-7 w-16" /><Skeleton className="h-3 w-32" /></CardContent></Card>
            ))}
          </div>
          <Card><CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <Skeleton className="h-5 w-56" /><Skeleton className="h-8 w-40" />
            </div>
            <Skeleton className="h-8 w-72 rounded-lg" />
            <Skeleton className="h-[380px] w-full rounded-md" />
          </CardContent></Card>
          <Card><CardContent className="p-6 space-y-3">
            <Skeleton className="h-5 w-44" />
            <Skeleton className="h-9 w-64 rounded-lg" />
            <div className="space-y-2 pt-2">
              {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
            </div>
          </CardContent></Card>
        </div>
      )}

      {isError && (
        <Card><CardContent className="p-6 flex items-center justify-between gap-4">
          <div>
            {/* A 429 is not a failure the user can retry their way out of, and saying "please try
                again" invites exactly the thing that caused it. The endpoint is rate-limited
                because each uncached search is a billed warehouse query (`api.php`), so the
                honest message names the wait. */}
            <p className="text-sm font-medium text-foreground">
              {throttled ? "Too many searches" : "Could not load keyword trends"}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              {throttled
                ? "You've searched a lot in a short time. Wait a moment, then try again."
                : "The search did not complete. Please try again."}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>Try again</Button>
        </CardContent></Card>
      )}

      {showEmpty && (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border bg-card px-6 py-16 text-center">
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-surface-tint-medium">
            <PackageOpen className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
          </div>
          <h4 className="text-lg font-semibold text-foreground">No keywords matched “{submitted?.term}”</h4>
          <p className="mt-1 max-w-md text-sm text-muted-foreground">
            Try a loose match or one of the suggested terms above.
          </p>
        </div>
      )}

      {showResults && (
        <>
          <div className="grid gap-4 md:grid-cols-3">
            {/* The design reference's own KPI captions. FR-3's "the screen says so" is carried by
                the chart subtitle below, which names the cap in full — putting it in the caption too
                made three of the six cards read differently from every other KPI row in the app. */}
            <Kpi
              label="Matching keywords"
              tip={capped
                ? `The ${data.limit} highest-volume keywords matching this search. More matched; this screen shows the largest.`
                : "Distinct keywords matching this search."}
              value={keywords.length.toLocaleString()}
            />
            <Kpi
              label="Total search volume (3 yr)"
              tip={capped
                ? `Summed weekly Amazon search volume across the last 156 weeks, for the top ${data.limit} matching keywords — not every keyword that matched.`
                : "Summed weekly Amazon search volume across the last 156 weeks."}
              value={fmt(totalVolume)}
            />
            <Kpi
              label="Avg weekly volume"
              tip="Average weekly search volume across the keywords shown."
              value={fmt(totals.length ? totalVolume / totals.length : 0)}
            />
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            {growth.map((w) => (
              <Kpi
                key={w.label}
                label={w.label}
                tip={`Search volume in the ${w.label.toLowerCase()} compared with the prior equivalent period.`}
                value={fmt(w.current)}
                change={w.change}
                changeLabel={`vs ${fmt(w.prior)} prior`}
              />
            ))}
          </div>

          <Card>
            <CardContent className="p-6 space-y-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-foreground">Search volume — “{data.term}”</h2>
                  <p className="mt-0.5 text-sm text-muted-foreground">
                    {/* FR-3 wants the cap named wherever the screen reports a total, and exact mode
                        can hit its own cap of 10 -- it groups on the raw keyword, so casing and
                        whitespace variants of one term count separately. Uncapped, both modes read
                        exactly as the design reference writes them. */}
                    {data.mode === "exact"
                      ? `Exact match${capped ? ` · top ${data.limit} by volume` : ""}`
                      : `Loose match (contains) · aggregated across ${keywords.length} keyword${keywords.length === 1 ? "" : "s"}${capped ? ` (top ${data.limit} by volume)` : ""}`}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <CopyVisualButton
                    targetRef={chartRef}
                    className="gap-1.5"
                    /* Only the year-over-year view needs one: the continuous view is a single
                       series, and its one line needs no key. */
                    legend={view === "overlay"
                      ? years.map((y, i) => ({ label: String(y), color: seriesColor(i) }))
                      : undefined}
                  />
                  <Button
                    variant="ghost" size="sm" className="gap-1.5"
                    onClick={() => {
                      exportKeywordTrends(rows, data.term);
                      toast.success("Export Complete", {
                        description: `Exported ${rows.length} keyword${rows.length === 1 ? "" : "s"} to Excel.`,
                      });
                    }}
                  >
                    <Download className="h-4 w-4" /> Export
                  </Button>
                </div>
              </div>

              <Tabs value={view} onValueChange={(v) => setView(v as "overlay" | "continuous")}>
                <TabsList>
                  <TabsTrigger value="overlay">Year over year</TabsTrigger>
                  <TabsTrigger value="continuous">Continuous timeline</TabsTrigger>
                </TabsList>
              </Tabs>

              <div ref={chartRef}>
              {view === "overlay" ? (
                <ChartContainer config={overlayConfig} className="aspect-auto w-full" style={{ height: 380 }}>
                  <LineChart data={overlay} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} minTickGap={32} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} tickFormatter={fmt} />
                    <ChartTooltip content={<ChartTooltipContent formatter={(v) => fmt(Number(v))} />} />
                    <ChartLegend content={<ChartLegendContent />} />
                    {years.map((y) => (
                      // connectNulls is deliberately OFF, and this is the one place this screen
                      // departs from the design reference's chart. A month with no data must break
                      // the line rather than be bridged -- bridging draws volume nobody measured,
                      // the same rule `components/rank-tracker.md` states for an unrecorded day.
                      //
                      // An earlier version of this comment claimed the gaps could only ever be
                      // leading or trailing. **That was wrong, and real warehouse data disproved
                      // it** (2026-09-23): individual keywords do have interior gaps -- `sunscreen
                      // kids` is missing most of Oct-Dec 2023, `face sunscreen for women` missing
                      // Jul-Oct 2024. What makes this setting rarely *visible* is that the chart
                      // plots the sum across the whole returned set, and a head term covers every
                      // week -- so the aggregate is dense even when its parts are not. Rarely
                      // visible is not never, which is exactly why it stays off.
                      <Line key={y} type="monotone" dataKey={String(y)} name={String(y)} stroke={`var(--color-${y})`} strokeWidth={2} dot={isolatedDot(overlay, String(y))} connectNulls={false} />
                    ))}
                  </LineChart>
                </ChartContainer>
              ) : (
                <ChartContainer config={CONTINUOUS_CONFIG} className="aspect-auto w-full" style={{ height: 380 }}>
                  <LineChart data={totals} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="week" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} minTickGap={32} tickFormatter={weekTick} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} tickFormatter={fmt} />
                    <ChartTooltip content={<ChartTooltipContent formatter={(v) => fmt(Number(v))} />} />
                    <Line type="monotone" dataKey="volume" name="Weekly volume" stroke="var(--color-volume)" strokeWidth={2} dot={isolatedDot(totals as unknown as Array<Record<string, string | number | null>>, "volume")} connectNulls={false} />
                  </LineChart>
                </ChartContainer>
              )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6 space-y-3">
              <h2 className="text-lg font-semibold text-foreground">Matching keywords</h2>

              <SearchFilterBar
                search={tableSearch}
                onSearchChange={setTableSearch}
                placeholder="Search keywords…"
                searchWidth="w-64"
              >
                <CombinedFilterDropdown
                  columns={[]}
                  activeFilters={{}}
                  onFiltersChange={() => {}}
                  rangeColumns={RANGE_FILTERS}
                  activeRangeFilters={rangeFilters}
                  onRangeFiltersChange={setRangeFilters}
                  textColumns={TEXT_FILTERS}
                  activeTextFilters={textFilters}
                  onTextFiltersChange={setTextFilters}
                />
                <span className="ml-auto text-sm text-muted-foreground">
                  Showing {rows.length} keyword{rows.length === 1 ? "" : "s"}
                </span>
              </SearchFilterBar>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Keyword</TableHead>
                    <TableHead className="text-right">Total volume</TableHead>
                    <TableHead className="text-right">Avg weekly</TableHead>
                    <TableHead className="text-right">Weeks observed</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="py-6 text-center text-xs text-muted-foreground">
                        No keywords match this search or filter.
                      </TableCell>
                    </TableRow>
                  ) : rows.map((r) => (
                    <TableRow key={r.keyword}>
                      <TableCell className="max-w-[320px] text-xs font-medium text-foreground">
                        <TruncatedText>{r.keyword}</TruncatedText>
                      </TableCell>
                      <TableCell className="text-right text-xs tabular-nums">{fmt(r.total)}</TableCell>
                      <TableCell className="text-right text-xs tabular-nums">{fmt(r.avg)}</TableCell>
                      <TableCell className="text-right text-xs tabular-nums">{r.weeks}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function Kpi({ label, tip, value, change, changeLabel }: {
  label: string; tip: string; value: string; change?: number | null; changeLabel?: string;
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="mb-3 flex items-start justify-between gap-2">
          <p className="text-sm text-muted-foreground">{label}</p>
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button" className="cursor-help text-muted-foreground" aria-label={`About ${label}`}>
                <Info className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-[200px]">{tip}</TooltipContent>
          </Tooltip>
        </div>
        <p className="text-2xl font-semibold tabular-nums tracking-tight text-foreground">{value}</p>
        {change != null && (
          <div className="mt-3 flex items-center gap-1.5">
            <Badge className={cn("gap-1 border-0 font-medium", change >= 0 ? "bg-status-success-soft text-foreground" : "bg-status-error-soft text-foreground")}>
              {change >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
              {/* One text node, not three -- so the percentage is selectable as written. */}
              <span>{`${change >= 0 ? "+" : ""}${change.toFixed(1)}%`}</span>
            </Badge>
            {changeLabel && <span className="text-xs tabular-nums text-muted-foreground">{changeLabel}</span>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
